import {
  computePosition,
  offset,
  flip,
  shift,
  autoUpdate,
  hide,
  size,
  type ComputePositionConfig,
  type Placement,
  type ReferenceElement,
} from "@floating-ui/dom";
export { type Placement } from "@floating-ui/dom";
export type { ReferenceElement } from "@floating-ui/dom";

export type FallbackPlacements = Placement[] | "no-flip";

type FloatingConfig = Partial<ComputePositionConfig> & {
  fallbackPlacements?: FallbackPlacements;
  offset?: number;
  /** The area flip, shift and size keep the panel inside, in place of every ancestor that clips it. */
  boundary?: Element;
  /**
   * Where no side fits the panel whole, slide it back inside the boundary over its reference instead of
   * capping it to what is left.
   */
  slideOverReference?: boolean;
  /** Poll every animation frame for position changes (e.g. virtual mouse-following references). */
  animationFrame?: boolean;
};

function updateFloatingPosition(
  reference: ReferenceElement,
  floating: HTMLElement,
  config?: FloatingConfig,
) {
  const {
    fallbackPlacements,
    offset: offsetValue = 8,
    boundary,
    slideOverReference,
    ...rest
  } = config || {};
  // Flip judges fit inside the same margin shift and size keep from the edge: judged against the edge
  // itself, a panel that fits only within the margin stays on that side, and size caps it a few pixels
  // short, which a scroll fade turns into a hidden last row.
  const area = { padding: 8, ...(boundary && { boundary }) };

  const middleware = [offset(offsetValue)];
  if (fallbackPlacements !== "no-flip") {
    middleware.push(flip({ fallbackPlacements, ...area }));
  }
  middleware.push(
    shift({ ...area, crossAxis: slideOverReference }),
    // Cap floating panels to the space actually available so a tall, scrollable
    // panel (e.g. a long dropdown) never overflows a short viewport/iframe with
    // its lower rows clipped and unreachable. Exposed as a CSS var that scrollable
    // contents opt into via `max-h-[min(...,var(--floating-available-height))]`.
    size({
      ...area,
      apply({ availableHeight, elements }) {
        elements.floating.style.setProperty(
          "--floating-available-height",
          `${Math.max(0, Math.round(availableHeight))}px`,
        );
      },
    }),
    // No padding: padding here means "treat as hidden when within Npx of the
    // clip edge", which false-positives every reference SHORTER than the
    // padding (a compact bar's 8px segments read as fully clipped, always).
    hide(),
  );

  // Each pass measures the panel at its natural height: left capped from the last pass, a panel whose
  // content grew (a popover turning into a form) would read as fitting where it was squeezed, and flip
  // would keep it there to scroll instead of taking the side with room. The cap comes back from `size`
  // in the same pass, before anything paints, and only bites when neither side fits.
  floating.style.removeProperty("--floating-available-height");
  computePosition(reference, floating, {
    placement: "bottom-start",
    strategy: "fixed",
    middleware,
    ...rest,
  }).then(({ x, y, middlewareData }) => {
    // Always position; hide via visibility when the reference is clipped away.
    // Skipping the assignment instead leaves a still-visible panel wherever it
    // happened to be — for a fresh popover, the viewport's top-left corner.
    Object.assign(floating.style, {
      left: `${x}px`,
      top: `${y}px`,
      visibility: middlewareData.hide?.referenceHidden ? "hidden" : "visible",
    });
  });
}

/** Creates floating positioning and returns a cleanup function. */
export function createFloating(options: {
  reference: ReferenceElement;
  floating: HTMLElement;
  config?: FloatingConfig;
}) {
  return autoUpdate(
    options.reference,
    options.floating,
    () => updateFloatingPosition(options.reference, options.floating, options.config),
    options.config?.animationFrame ? { animationFrame: true } : undefined,
  );
}
